import { useEffect, useMemo, useState } from "react";
import { Modal } from "../../../components/ui/modal";
import Button from "../../../components/ui/button/Button";
import { Table, TableBody, TableCell, TableHeader, TableRow } from "../../../components/ui/table";

type AddInspectionItemPayload = {
  inspectiontask_id: string | number;
  task_name: string;
  heading_name: string;
};

type AddInspectionItemModalProps = {
  isOpen: boolean;
  onClose: () => void;
  selectedInspectionId: string | number | null;
  selectedVenueId: string | number | null;
  onSaved: () => Promise<void> | void;
};

const apiUrl = process.env.REACT_APP_API_URL;

export default function AddInspectionItemModal({
  isOpen,
  onClose,
  selectedInspectionId,
  selectedVenueId,
  onSaved,
}: AddInspectionItemModalProps) {
  const token = localStorage.getItem("token");
  const [availableItems, setAvailableItems] = useState<any[]>([]);
  const [selectedIds, setSelectedIds] = useState<Array<string | number>>([]);
  const [loading, setLoading] = useState(false);

  const handleClose = () => {
    setSelectedIds([]);
    onClose();
  };

  useEffect(() => {
    if (!isOpen || !selectedInspectionId) return;

    const fetchInspectionItems = async () => {
      setLoading(true);
      try {
        const headers = {
          "Content-Type": "application/json",
          token: token || "",
        };

        let response = await fetch(`${apiUrl}/inspection_history/items/addable/${selectedInspectionId}`, {
          headers,
        });

        if (response.status === 404) {
          response = await fetch(`${apiUrl}/inspection-history/items/addable/${selectedInspectionId}`, {
            headers,
          });
        }

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const result = await response.json();
        if (result.success) {
          setAvailableItems(result.inspectionItems || []);
        } else {
          setAvailableItems([]);
        }
      } catch (error) {
        console.error("Error fetching inspection items:", error);
        setAvailableItems([]);
      } finally {
        setLoading(false);
      }
    };

    fetchInspectionItems();
  }, [isOpen, selectedInspectionId, token]);

  const selectedRows = useMemo(
    () =>
      availableItems
        .filter((item) => selectedIds.includes(item.inspectiontask_id))
        .map((item) => ({
          inspectiontask_id: item.inspectiontask_id,
          task_name: item.name ?? item.inspectionitem_name ?? "",
          heading_name: item.heading ?? item.heading_name ?? "",
        })),
    [availableItems, selectedIds]
  );

  const toggleSelect = (id: string | number) => {
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((itemId) => itemId !== id) : [...prev, id]
    );
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (selectedRows.length === 0 || !selectedInspectionId) return;

    try {
      for (const selectedItem of selectedRows) {
        const response = await fetch(`${apiUrl}/inspection_history/items`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            token: token || "",
          },
          body: JSON.stringify({
            compinspection_id: selectedInspectionId,
            inspectiontask_id: selectedItem.inspectiontask_id,
            cst: selectedVenueId,
          }),
        });

        if (!response.ok) {
          throw new Error(`HTTP ${response.status}`);
        }
      }

      await onSaved();
      handleClose();
    } catch (error) {
      console.error("Failed to save inspection items:", error);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={handleClose} className="max-w-5xl p-6 m-4">
      <h4 className="mb-4 text-lg font-semibold text-gray-900 dark:text-white">
        Add Inspection Item
      </h4>

      <form onSubmit={handleSubmit}>
        <div className="max-h-[420px] overflow-auto">
          <Table>
            <TableHeader className="bg-[#d9ffa3]">
              <TableRow>
                <TableCell isHeader className="w-10 px-2 py-2 border border-gray-100 dark:border-white/[0.05]">
                  {" "}
                </TableCell>
                <TableCell isHeader className="px-4 py-3 border border-gray-100 dark:border-white/[0.05]">
                  Inspection Item
                </TableCell>
                <TableCell isHeader className="px-4 py-3 border border-gray-100 dark:border-white/[0.05]">
                  Heading
                </TableCell>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading && (
                <TableRow>
                  <td className="px-4 py-4 text-xs text-center text-gray-500 border border-gray-100 dark:border-white/[0.05]" colSpan={3}>
                    Loading...
                  </td>
                </TableRow>
              )}
              {!loading && availableItems.length === 0 && (
                <TableRow>
                  <td className="px-4 py-4 text-xs text-center text-gray-500 border border-gray-100 dark:border-white/[0.05]" colSpan={3}>
                    No records to view
                  </td>
                </TableRow>
              )}
              {!loading &&
                availableItems.map((item) => {
                  const isSelected = selectedIds.includes(item.inspectiontask_id);
                  return (
                    <TableRow
                      key={item.inspectiontask_id}
                      className={`cursor-pointer ${isSelected ? "bg-[#ffffa3]" : "hover:bg-[#ffffa3]"}`}
                      onClick={() => toggleSelect(item.inspectiontask_id)}
                    >
                      <TableCell className="px-4 py-4 border border-gray-100 dark:border-white/[0.05] text-xs">
                        <input
                          type="checkbox"
                          checked={isSelected}
                          onChange={() => toggleSelect(item.inspectiontask_id)}
                          onClick={(e) => e.stopPropagation()}
                        />
                      </TableCell>
                      <TableCell className="px-4 py-4 font-normal text-gray-800 border border-gray-100 dark:border-white/[0.05] text-xs dark:text-gray-400 whitespace-normal">
                        {item.name ?? item.inspectionitem_name ?? ""}
                      </TableCell>
                      <TableCell className="px-4 py-4 font-normal text-gray-800 border border-gray-100 dark:border-white/[0.05] text-xs dark:text-gray-400 whitespace-normal">
                        {item.heading ?? item.heading_name ?? ""}
                      </TableCell>
                    </TableRow>
                  );
                })}
            </TableBody>
          </Table>
        </div>

        <div className="mt-4 flex justify-end gap-3">
          <Button type="button" size="sm" variant="outline" onClick={handleClose}>
            Cancel
          </Button>
          <Button type="submit" size="sm">
            Save Changes
          </Button>
        </div>
      </form>
    </Modal>
  );
}
